import { Avatar, Box, Button, Chip, IconButton, LinearProgress, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import LaunchTwoToneIcon from "@mui/icons-material/LaunchTwoTone";
import { STATUS_STYLES } from "../CompanyManagementLayout/CompanyHeader/CompanyHeader.config";
import { IProspectList } from "../../Interfaces/IProspectList";
import { SortValueType, emDashWhenEmpty, formatNzCurrency } from "../../Tools/GridSortUtils";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";

// How each sortable column compares (P.8: numbers numerically, dates chronologically).
export const PROSPECT_SORT_TYPES: Record<string, SortValueType> = {
  companyName: "string",
  onboardingCompletedSteps: "number",
  status: "string",
  franchiseName: "string",
  tddaRelationshipManager: "string",
  salesTotalValue: "number",
  inProgressActions: "number",
  overdueActions: "number",
  companyIntegrationStatus: "string",
  dealStage: "string",
  lastModifiedBy: "string",
  createdOn: "date",
  updatedOn: "date",
};

const emDash = (params: { value?: any }) => emDashWhenEmpty(params.value);

const dateFormatter = (params: { value?: any }) =>
  params.value ? DateTimeUtils.dateToLocalDateZone(params.value) : "—";

const actionCountCell = (highlight: string) => (params: any) =>
  (
    <Avatar sx={{ backgroundColor: params.value > 0 && highlight }}>
      {params.value}
    </Avatar>
  );

/**
 * The Prospects columns (P.1): onboarding columns leading, every Home column retained, none dropped
 * or renamed — plus an Overview column (task 5861), so a click that shouldn't open the full record
 * (the same reason the action column carries its own stopPropagation) still has a one-click way in.
 * Home's own Edit column has no equivalent here: the action column already opens the wizard, the one
 * thing a prospect-stage client's own "Edit" ever means.
 */
export const prospectColumns = (
  onAction: (row: IProspectList) => void,
): GridColDef[] => [
  {
    field: "companyName",
    headerName: "Company Name",
    width: 200,
    editable: false,
  },
  {
    field: "onboardingCompletedSteps",
    headerName: "Onboarding Progress",
    width: 160,
    editable: false,
    renderCell: (params) => {
      const row = params.row as IProspectList;
      return (
        <Box sx={{ width: "100%", pr: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={(row.onboardingCompletedSteps / row.onboardingTotalSteps) * 100}
              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2">
              {row.onboardingCompletedSteps}/{row.onboardingTotalSteps}
            </Typography>
          </Box>
        </Box>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    editable: false,
    renderCell: (params) => {
      if (!params.value) return "—";
      const styles =
        STATUS_STYLES[`${params.value}`.toLowerCase()] || STATUS_STYLES["default"];
      return (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: styles.chipBg,
            color: styles.chipColor,
            fontWeight: 600,
          }}
        />
      );
    },
  },
  {
    field: "onboardingAction",
    headerName: "Action",
    width: 160,
    editable: false,
    sortable: false,
    renderCell: (params) => {
      const row = params.row as IProspectList;
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onAction(row);
          }}
        >
          {/* A submitted onboarding is frozen, so the grid offers to view it rather than inviting an
              edit — the same three labels the record Overview uses (CR.2, bug 5715). Where it lands
              is unchanged: step 7, which renders the submitted panel. */}
          {row.isOnboardingSubmitted
            ? "View Onboarding"
            : row.onboardingCompletedSteps === 0
              ? "Start Onboarding"
              : "Continue"}
        </Button>
      );
    },
  },
  {
    field: "overview",
    headerName: "Overview",
    width: 100,
    editable: false,
    sortable: false,
    renderCell: (params) => {
      const row = params.row as IProspectList;
      return (
        <IconButton
          color="primary"
          component={Link}
          to={`/company/${row.companyRowID}/overview`}
          onClick={(e) => e.stopPropagation()}
        >
          <LaunchTwoToneIcon />
        </IconButton>
      );
    },
  },
  {
    field: "franchiseName",
    headerName: "TDDA Supporting Region",
    width: 185,
    editable: false,
    valueFormatter: emDash,
  },
  {
    field: "tddaRelationshipManager",
    headerName: "TDDA Relationship Manager",
    width: 220,
    editable: false,
    valueFormatter: emDash,
  },
  {
    field: "salesTotalValue",
    headerName: "Sales Pipeline Value",
    width: 140,
    editable: false,
    valueFormatter: (params) => formatNzCurrency(params.value),
  },
  {
    field: "inProgressActions",
    headerName: "In Progress Action",
    width: 130,
    editable: false,
    renderCell: actionCountCell("orange"),
  },
  {
    field: "overdueActions",
    headerName: "Overdue Action",
    width: 110,
    editable: false,
    renderCell: actionCountCell("red"),
  },
  {
    field: "companyIntegrationStatus",
    headerName: "Imperans Integration",
    width: 145,
    editable: false,
    valueFormatter: emDash,
  },
  {
    field: "dealStage",
    headerName: "Deal Stage",
    width: 145,
    editable: false,
    // Derived summary text ("N Deals in Progress") — not a sortable value; matches Home.
    sortable: false,
    valueFormatter: emDash,
  },
  {
    field: "lastModifiedBy",
    headerName: "Modified By",
    width: 150,
    editable: false,
    valueFormatter: emDash,
  },
  {
    field: "createdOn",
    headerName: "Created On",
    width: 95,
    editable: false,
    valueFormatter: dateFormatter,
  },
  {
    field: "updatedOn",
    headerName: "Updated On",
    width: 95,
    editable: false,
    valueFormatter: dateFormatter,
  },
];
